import { Button, Checkbox, Form, Input, InputNumber, Message, Modal, Select, Space, Switch, Table, Tabs, Tag } from "@arco-design/web-react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { getCityOptions, getProvinceOptions } from "../../../shared/region";
import { marketRegionMemberRoleLabels, marketRegionScopeTypeLabels, marketRegionTypeLabels } from "../config";
import {
  emptyMarketRegionDraft,
  useCrmMarketRegionRuntime,
  useCrmMarketRegionScopeOptions,
} from "../runtime/useCrmMarketRegionRuntime";
import {
  buildMarketRegionScopeSummary,
  buildScopeDisplayName,
  formatMarketRegionMemberRole,
  formatMarketRegionScopeType,
  formatMarketRegionType,
} from "../service/crmMarketRegionService";
import type {
  MarketRegionItem,
  MarketRegionMemberItem,
  MarketRegionMutation,
  MarketRegionScopeItem,
  MarketRegionScopeType,
} from "../types";
import "./CrmMarketRegionPage.css";
import "../../../shared/ui/crmListPageLayout.css";

/** CRM 区域经营配置页面。 */
export function CrmMarketRegionFeaturePage() {
  const runtime = useCrmMarketRegionRuntime();
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "crm.region_config:manage");
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<MarketRegionItem | null>(null);
  const [regionDraft, setRegionDraft] = useState<MarketRegionMutation>(emptyMarketRegionDraft);
  const [scopeType, setScopeType] = useState<MarketRegionScopeType>("province");
  const [scopeProvince, setScopeProvince] = useState("");
  const [scopeProvinces, setScopeProvinces] = useState<string[]>([]);
  const [scopeCity, setScopeCity] = useState("");
  const [scopeOrganizationId, setScopeOrganizationId] = useState<number>();
  const [scopeOrgUnitId, setScopeOrgUnitId] = useState<number>();
  const [scopeName, setScopeName] = useState("");
  const [organizationKeyword, setOrganizationKeyword] = useState("");
  const [orgUnitKeyword, setOrgUnitKeyword] = useState("");
  const [memberUserId, setMemberUserId] = useState<number>();
  const [memberRole, setMemberRole] = useState<"owner" | "manager" | "viewer">("owner");
  const cityOptions = useMemo(() => getCityOptions(scopeProvince), [scopeProvince]);
  const provinceOptions = useMemo(() => getProvinceOptions(), []);
  const scopeOptions = useCrmMarketRegionScopeOptions({
    scopeType,
    province: scopeProvince || undefined,
    city: scopeCity || undefined,
    organizationId: scopeOrganizationId,
    organizationKeyword,
    orgUnitKeyword,
  });
  const selectedOrganization = scopeOptions.organizationOptions.find((item) => item.id === scopeOrganizationId);
  const selectedOrgUnit = scopeOptions.orgUnitOptions.find((item) => item.id === scopeOrgUnitId);
  const scopeSummary = useMemo(() => buildMarketRegionScopeSummary(runtime.scopes), [runtime.scopes]);
  const existingProvincesForMode = useMemo(() => {
    return runtime.scopes
      .filter((scope) => scope.scopeType === "province" && Boolean(scope.province) && !scope.isExcluded)
      .map((scope) => scope.province as string)
      .sort();
  }, [runtime.scopes]);
  const existingProvinceSet = useMemo(() => new Set(existingProvincesForMode), [existingProvincesForMode]);
  const provincesToAdd = useMemo(
    () => scopeProvinces.filter((province) => !existingProvinceSet.has(province)),
    [existingProvinceSet, scopeProvinces],
  );
  const provinceScopeIdsToDelete = useMemo(() => {
    const selectedSet = new Set(scopeProvinces);
    return runtime.scopes
      .filter(
        (scope) =>
          scope.scopeType === "province" &&
          !scope.isExcluded &&
          Boolean(scope.province) &&
          !selectedSet.has(scope.province as string),
      )
      .map((scope) => scope.id);
  }, [runtime.scopes, scopeProvinces]);
  useEffect(() => {
    if (scopeType !== "province") {
      return;
    }
    // 切换区域或服务端范围变化时，严格回显当前区域已配置省份，不得保留上一区域勾选。
    setScopeProvinces((previous) => {
      if (
        previous.length === existingProvincesForMode.length &&
        previous.every((province, index) => province === existingProvincesForMode[index])
      ) {
        return previous;
      }
      return existingProvincesForMode;
    });
  }, [existingProvincesForMode, runtime.selectedRegionId, scopeType]);
  const needsCity = scopeType === "city" || scopeType === "organization" || scopeType === "organization_unit";
  const needsOrganization = scopeType === "organization" || scopeType === "organization_unit";
  const needsOrgUnit = scopeType === "organization_unit";
  const needsSnapshotName = scopeType === "customer" || scopeType === "contact";
  const isProvinceMultiSelect = scopeType === "province";
  const canSubmitScope = Boolean(
    runtime.selectedRegionId &&
      (isProvinceMultiSelect ||
        (scopeType === "city" && scopeProvince && scopeCity) ||
        (scopeType === "organization" && scopeProvince && scopeCity && scopeOrganizationId) ||
        (scopeType === "organization_unit" && scopeProvince && scopeCity && scopeOrganizationId && scopeOrgUnitId) ||
        (needsSnapshotName && scopeName.trim())),
  );

  /** 打开新增区域弹窗。 */
  function openCreateRegion() {
    setEditingRegion(null);
    setRegionDraft({ ...emptyMarketRegionDraft, currentUserId: runtime.currentUserId });
    setRegionModalOpen(true);
  }

  /** 打开编辑区域弹窗并回填当前区域。 */
  function openEditRegion(region: MarketRegionItem) {
    setEditingRegion(region);
    setRegionDraft({
      regionName: region.regionName,
      regionCode: region.regionCode ?? "",
      regionType: region.regionType,
      description: region.description ?? "",
      isDisabled: region.isDisabled,
      currentUserId: runtime.currentUserId,
      remark: region.remark ?? "",
    });
    setRegionModalOpen(true);
  }

  /** 保存区域新增或编辑内容。 */
  async function saveRegion() {
    if (editingRegion) {
      await runtime.handleUpdateRegion(editingRegion, regionDraft);
    } else {
      await runtime.handleCreateRegion(regionDraft);
    }
    setRegionModalOpen(false);
  }

  /** 切换范围类型时清空下级选择，避免旧值污染新范围。 */
  function handleScopeTypeChange(value: MarketRegionScopeType) {
    setScopeType(value);
    resetScopeSelection();
  }

  /** 切换省份时清空城市、学校、院系。 */
  function handleProvinceChange(value?: string) {
    setScopeProvince(value ?? "");
    setScopeCity("");
    clearOrganizationSelection();
  }

  /** 切换城市时清空学校、院系。 */
  function handleCityChange(value?: string) {
    setScopeCity(value ?? "");
    clearOrganizationSelection();
  }

  /** 切换学校时清空院系。 */
  function handleOrganizationChange(value?: number) {
    setScopeOrganizationId(value);
    setScopeOrgUnitId(undefined);
    setOrgUnitKeyword("");
  }

  /** 清空范围选择。 */
  function resetScopeSelection() {
    setScopeProvince("");
    setScopeProvinces([]);
    setScopeCity("");
    setScopeName("");
    clearOrganizationSelection();
  }

  /** 清空学校与院系选择。 */
  function clearOrganizationSelection() {
    setScopeOrganizationId(undefined);
    setScopeOrgUnitId(undefined);
    setOrganizationKeyword("");
    setOrgUnitKeyword("");
  }

  /** 为当前选中区域添加或按勾选同步省份范围。 */
  async function addScope() {
    if (isProvinceMultiSelect && provincesToAdd.length === 0 && provinceScopeIdsToDelete.length === 0) {
      return;
    }
    if (!isProvinceMultiSelect && !canSubmitScope) {
      return;
    }

    const closeLoading = showScopeSavingMessage(
      isProvinceMultiSelect ? "正在同步范围，请稍候…" : "正在添加范围，请稍候…",
    );
    try {
      if (isProvinceMultiSelect) {
        await runtime.handleSyncProvinceScopes({
          payloads: provincesToAdd.map((province) => ({
            scopeType,
            country: "中国",
            province,
            scopeNameSnapshot: province,
            isExcluded: false,
          })),
          scopeIds: provinceScopeIdsToDelete,
        });
        return;
      }

      const scopeNameSnapshot =
        scopeType === "organization"
          ? selectedOrganization?.orgName
          : scopeType === "organization_unit"
            ? [selectedOrganization?.orgName, selectedOrgUnit?.unitPathName].filter(Boolean).join("/")
            : scopeName.trim() || scopeCity || scopeProvince || undefined;
      await runtime.handleCreateScope({
        scopeType,
        country: "中国",
        province: scopeProvince || undefined,
        city: scopeCity || undefined,
        orgId: scopeOrganizationId,
        orgUnitId: scopeOrgUnitId,
        scopeNameSnapshot,
        isExcluded: false,
      });
      resetScopeSelection();
    } finally {
      closeLoading();
    }
  }

  /** 确认后删除当前区域范围。 */
  function confirmDeleteScope(scope: MarketRegionScopeItem) {
    Modal.confirm({
      title: "删除范围",
      content: `确认删除「${buildScopeDisplayName(scope)}」吗？`,
      okText: "删除",
      cancelText: "取消",
      onOk: () => runtime.handleDeleteScope(scope.id),
    });
  }

  /** 确认后删除当前区域成员。 */
  function confirmDeleteMember(member: MarketRegionMemberItem) {
    const memberName = member.userName || `员工 ${member.userId}`;
    Modal.confirm({
      title: "删除成员",
      content: `确认删除「${memberName}」吗？`,
      okText: "删除",
      cancelText: "取消",
      onOk: () => runtime.handleDeleteMember(member.id),
    });
  }

  /** 为当前选中区域添加或更新成员。 */
  async function addMember() {
    const targetUserId = memberUserId ?? runtime.staffOptions.find((item) => !item.disabled)?.id;
    if (!targetUserId) {
      return;
    }
    await runtime.handleCreateMember({
      userId: targetUserId,
      memberRole,
      canAssign: true,
      canEditScope: memberRole !== "viewer",
      canViewStats: true,
      isDisabled: false,
    });
  }

  return (
    <main className="crm-list-page crm-market-region-page">
      {runtime.errorMessage ? <section role="alert">{runtime.errorMessage}</section> : null}

      <section className="crm-market-region-grid">
        <section className="crm-list-panel crm-market-region-list">
          <div className="crm-list-section-header">
            <h2>区域列表</h2>
            <div className="crm-list-table-actions">
              <Space>
                <InputNumber
                  min={1}
                  value={runtime.currentUserId}
                  onChange={(value) => runtime.setCurrentUserId(Number(value || 1))}
                />
                <Button type="primary" disabled={!canManage} onClick={openCreateRegion}>
                  新建区域
                </Button>
              </Space>
            </div>
          </div>
          <div className="crm-list-panel-body">
          <Space style={{ marginBottom: 12 }}>
            <Input
              allowClear
              placeholder="区域名称/编码"
              value={runtime.keyword}
              onChange={runtime.setKeyword}
            />
          </Space>
          <Table
            rowKey="id"
            loading={runtime.loading}
            data={runtime.regions}
            pagination={{
              current: runtime.page,
              pageSize: runtime.pageSize,
              total: runtime.total,
              onChange: runtime.handlePageChange,
            }}
            onRow={(record) => ({
              onClick: () => runtime.setSelectedRegionId(record.id),
            })}
            columns={[
              {
                title: "区域名称",
                dataIndex: "regionName",
                render: (_, record) => (
                  <Button type="text" onClick={() => runtime.setSelectedRegionId(record.id)}>
                    {record.regionName}
                  </Button>
                ),
              },
              {
                title: "类型",
                dataIndex: "regionType",
                render: (value) => formatMarketRegionType(value),
              },
              {
                title: "状态",
                dataIndex: "isDisabled",
                render: (_, record) => (
                  <Tag color={record.isDisabled ? "gray" : "green"}>{record.isDisabledLabel ?? "启用"}</Tag>
                ),
              },
              {
                title: "操作",
                render: (_, record) => (
                  <Space>
                    <Button size="mini" disabled={!canManage} onClick={() => openEditRegion(record)}>
                      编辑
                    </Button>
                    <Switch
                      checked={!record.isDisabled}
                      disabled={!canManage}
                      onChange={(checked) => void runtime.handleUpdateStatus(record, !checked)}
                    />
                  </Space>
                ),
              },
            ]}
          />
          </div>
        </section>

        <section className="crm-list-panel crm-market-region-detail">
          <div className="crm-list-section-header">
            <h2>{runtime.selectedRegion?.regionName ?? "请选择区域"}</h2>
          </div>
          <div className="crm-list-panel-body">
          <Tabs defaultActiveTab="scopes">
            <Tabs.TabPane key="scopes" title="范围设置">
              <div className="crm-market-region-inline-form">
                <div className="crm-market-region-scope-fields">
                  <Select
                    data-testid="market-region-scope-type-select"
                    value={scopeType}
                    onChange={(value) => handleScopeTypeChange(value as MarketRegionScopeType)}
                    style={{ width: 132 }}
                  >
                    {Object.entries(marketRegionScopeTypeLabels).map(([value, label]) => (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                  {!needsSnapshotName && isProvinceMultiSelect ? (
                    <div
                      className="crm-market-region-province-multi"
                      data-testid="market-region-scope-province-multi"
                    >
                      <Checkbox.Group
                        disabled={!canManage || runtime.mutating}
                        options={provinceOptions}
                        value={scopeProvinces}
                        onChange={(values) => setScopeProvinces(values as string[])}
                      />
                    </div>
                  ) : null}
                  {!needsSnapshotName && !isProvinceMultiSelect ? (
                    <Select
                      allowClear
                      showSearch
                      data-testid="market-region-scope-province-select"
                      placeholder="省份"
                      value={scopeProvince || undefined}
                      onChange={(value) => handleProvinceChange(value as string | undefined)}
                      style={{ width: 150 }}
                    >
                      {provinceOptions.map((province) => (
                        <Select.Option key={province} value={province}>
                          {province}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : null}
                  {needsCity ? (
                    <Select
                      allowClear
                      showSearch
                      data-testid="market-region-scope-city-select"
                      disabled={!scopeProvince}
                      placeholder="城市"
                      value={scopeCity || undefined}
                      onChange={(value) => handleCityChange(value as string | undefined)}
                      style={{ width: 150 }}
                    >
                      {cityOptions.map((city) => (
                        <Select.Option key={city} value={city}>
                          {city}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : null}
                  {needsOrganization ? (
                    <Select
                      allowClear
                      showSearch
                      data-testid="market-region-scope-organization-select"
                      disabled={!scopeProvince || !scopeCity}
                      filterOption={false}
                      loading={scopeOptions.organizationLoading}
                      placeholder="学校"
                      value={scopeOrganizationId}
                      onChange={(value) => handleOrganizationChange(value as number | undefined)}
                      onSearch={setOrganizationKeyword}
                      style={{ width: 220 }}
                    >
                      {scopeOptions.organizationOptions.map((organization) => (
                        <Select.Option key={organization.id} value={organization.id}>
                          {organization.orgName}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : null}
                  {needsOrgUnit ? (
                    <Select
                      allowClear
                      showSearch
                      data-testid="market-region-scope-org-unit-select"
                      disabled={!scopeOrganizationId}
                      filterOption={false}
                      loading={scopeOptions.orgUnitLoading}
                      placeholder="院系/专业"
                      value={scopeOrgUnitId}
                      onChange={(value) => setScopeOrgUnitId(value as number | undefined)}
                      onSearch={setOrgUnitKeyword}
                      style={{ width: 220 }}
                    >
                      {scopeOptions.orgUnitOptions.map((unit) => (
                        <Select.Option key={unit.id} value={unit.id}>
                          {unit.unitPathName}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : null}
                  {needsSnapshotName ? (
                    <Input placeholder="范围快照名" value={scopeName} onChange={setScopeName} />
                  ) : null}
                </div>
                <Button
                  type="primary"
                  loading={runtime.mutating}
                  disabled={!canManage || !canSubmitScope || runtime.mutating}
                  onClick={() => void addScope()}
                >
                  添加/修改范围
                </Button>
              </div>
              {scopeSummary.length > 0 ? (
                <section className="crm-market-region-scope-summary" aria-label="范围表达">
                  <span className="crm-market-region-scope-summary-title">范围表达</span>
                  <div className="crm-market-region-scope-summary-tree" role="tree">
                    {scopeSummary.map((group) => (
                      <div key={group.key} className="crm-market-region-scope-summary-node" role="treeitem">
                        <div className="crm-market-region-scope-summary-head">
                          <span className="crm-market-region-scope-summary-group-title">{group.title}</span>
                          {group.hasWholeInclude ? (
                            <Tag
                              color="green"
                              data-testid={`market-region-scope-summary-whole-${group.title}`}
                              size="small"
                            >
                              包含
                            </Tag>
                          ) : null}
                        </div>
                        {group.includedItems.length > 0 ? (
                          <span className="crm-market-region-scope-summary-line">
                            包含：{group.includedItems.join("、")}
                          </span>
                        ) : null}
                        {group.excludedItems.length > 0 ? (
                          <span className="crm-market-region-scope-summary-line crm-market-region-scope-summary-line-muted">
                            排除：{group.excludedItems.join("、")}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
              <Table
                rowKey="id"
                data={runtime.scopes}
                pagination={false}
                columns={[
                  { title: "范围类型", dataIndex: "scopeType", render: (value) => formatMarketRegionScopeType(value) },
                  { title: "范围", render: (_, record) => buildScopeDisplayName(record) },
                  {
                    title: "操作",
                    render: (_, record) => (
                      <Button
                        data-testid={`market-region-delete-scope-${record.id}`}
                        size="mini"
                        status="danger"
                        type="text"
                        disabled={!canManage}
                        onClick={() => confirmDeleteScope(record)}
                      >
                        删除
                      </Button>
                    ),
                  },
                ]}
              />
            </Tabs.TabPane>
            <Tabs.TabPane key="members" title="成员设置">
              <div className="crm-market-region-member-form">
                <Select
                  data-testid="market-region-member-staff-select"
                  allowClear
                  showSearch
                  value={memberUserId}
                  placeholder="选择员工"
                  filterOption={(inputValue, option) =>
                    String(option.props.children ?? "")
                      .toLowerCase()
                      .includes(inputValue.trim().toLowerCase())
                  }
                  onChange={(value) =>
                    setMemberUserId(value === undefined || value === null ? undefined : Number(value))
                  }
                >
                  {runtime.staffOptions.map((staff) => (
                    <Select.Option key={staff.id} value={staff.id} disabled={staff.disabled}>
                      {staff.displayName}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={memberRole} onChange={(value) => setMemberRole(value as typeof memberRole)}>
                  {Object.entries(marketRegionMemberRoleLabels).map(([value, label]) => (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
                <Button type="primary" disabled={!canManage || !runtime.selectedRegionId} onClick={() => void addMember()}>
                  添加成员
                </Button>
              </div>
              <Table
                rowKey="id"
                data={runtime.members}
                pagination={false}
                columns={[
                  { title: "员工", dataIndex: "userName", render: (value, record) => value || record.userId },
                  { title: "角色", dataIndex: "memberRole", render: (value) => formatMarketRegionMemberRole(value) },
                  { title: "可分配", dataIndex: "canAssign", render: (value) => (value ? "是" : "否") },
                  { title: "可维护范围", dataIndex: "canEditScope", render: (value) => (value ? "是" : "否") },
                  {
                    title: "操作",
                    render: (_, record) => (
                      <Button
                        data-testid={`market-region-delete-member-${record.id}`}
                        size="mini"
                        status="danger"
                        type="text"
                        disabled={!canManage}
                        onClick={() => confirmDeleteMember(record)}
                      >
                        删除
                      </Button>
                    ),
                  },
                ]}
              />
            </Tabs.TabPane>
          </Tabs>
          </div>
        </section>
      </section>

      <Modal
        title={editingRegion ? "编辑区域" : "新建区域"}
        visible={regionModalOpen}
        okText="保存区域"
        confirmLoading={runtime.mutating}
        onOk={() => void saveRegion()}
        onCancel={() => setRegionModalOpen(false)}
      >
        <Form layout="vertical">
          <Form.Item label="区域名称" required>
            <Input
              aria-label="区域名称"
              value={regionDraft.regionName}
              onChange={(regionName) => setRegionDraft({ ...regionDraft, regionName })}
            />
          </Form.Item>
          <Form.Item label="区域类型">
            <Select
              value={regionDraft.regionType}
              onChange={(regionType) => setRegionDraft({ ...regionDraft, regionType })}
            >
              {Object.entries(marketRegionTypeLabels).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="区域编码">
            <Input
              value={regionDraft.regionCode}
              onChange={(regionCode) => setRegionDraft({ ...regionDraft, regionCode })}
            />
          </Form.Item>
          <Form.Item label="说明">
            <Input.TextArea
              value={regionDraft.description}
              onChange={(description) => setRegionDraft({ ...regionDraft, description })}
            />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
}

/** 展示范围保存中的全局反馈；关闭函数异常时不影响主流程。 */
function showScopeSavingMessage(content: string) {
  try {
    return Message.loading({
      id: "crm-market-region-scope-saving",
      content,
      duration: 0,
    });
  } catch {
    return () => undefined;
  }
}
